import {Badge, CircularProgress, IconButton, Tooltip, Typography} from '@mui/material';
import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import {byteArrayToImg, saveByteArray} from '../../fromKotlin/nkutils';
import DownloadIcon from '@mui/icons-material/Download';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import DocumentPreview from "../../views/customer/profile/documents/DocumentPreview";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {UploadFile} from '@mui/icons-material';
import {ExtendedDocument} from "../../types/DocumentsTypes/Document";
import {refreshDocs} from "../../hooks/customer/documents.hooks";
import {memo} from "react";
import {
    deleteDocumentPhoto,
    getDocumentPhotos,
    getLastLoadedFieldPhotos,
    looksLikeImage,
    MAX_DOC_PHOTOS,
    uploadDocumentPhoto,
} from "../../api/customer/documents.api";
import {fileToDocumentPhotoBytes, isPdfFile} from "../../utils/pdfToJpeg";

export function FieldPhotoCameraIcon({count, fontSize}: { count: number; fontSize?: number | string }) {
    const icon = (
        <CameraAltIcon style={{color: count > 0 ? 'green' : 'gray', fontSize}}/>
    );
    if (count <= 1) return icon;
    return (
        <Badge badgeContent={count} color="success" max={MAX_DOC_PHOTOS} overlap="circular">
            {icon}
        </Badge>
    );
}

function ShowImageDialog({
                             title,
                             subtitle,
                             icon,
                             tooltip,
                             calcFunction,
                             docs,
                             setWithPhoto,
                             onPhotoCountChange,
                             pool,
                         }: {
    title: string,
    subtitle: string,
    icon,
    tooltip,
    calcFunction: (d) => Promise<any>,
    docs?: ExtendedDocument[],
    setWithPhoto?: (b: boolean) => void,
    onPhotoCountChange?: (n: number) => void,
    pool?: string,
}) {
    const photoDocs = docs && docs.length > 0 ? docs : undefined;
    const photoMode = !!photoDocs;
    const uploadInputRef = React.useRef<HTMLInputElement>(null);
    const [open, setOpen] = React.useState(false);
    const [imgSrc, setImgSrc] = React.useState<string | undefined>(undefined);
    const [rotate, setRotate] = React.useState<number>(0);
    const [pos, setPos] = React.useState<number>(0);
    const [photoIdx, setPhotoIdx] = React.useState<number>(0);
    const [photos, setPhotos] = React.useState<any[]>([]);
    const [imgByteArray, setImgByteArray] = React.useState<any | undefined>(undefined);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [converting, setConverting] = React.useState<boolean>(false);
    const [reload, setReload] = React.useState<number>(0);
    const refresh = refreshDocs(pool);

    const currentDoc = photoDocs ? photoDocs[pos] : undefined;
    const remaining = Math.max(0, MAX_DOC_PHOTOS - photos.length);
    const busy = loading || converting;
    const manyPhotos = photoMode && photos.length > 1;

    const showPhoto = React.useCallback((bytes) => {
        if (bytes == null) {
            setImgSrc(undefined);
            setImgByteArray(undefined);
            return;
        }
        setImgSrc(byteArrayToImg(bytes));
        setImgByteArray(bytes);
    }, []);

    const handleClickOpen = React.useCallback(() => {
        setPos(0);
        setPhotoIdx(0);
        setRotate(0);
        setPhotos([]);
        setImgSrc(undefined);
        setImgByteArray(undefined);
        setOpen(true);
    }, []);

    const handleClose = () => {
        setOpen(false);
    };

    // One request per opened document: it returns the first photo and, for the
    // multi-photo path, leaves the rest on the Kotlin side.
    React.useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setLoading(true);
        (async () => {
            let first;
            try {
                first = await calcFunction(currentDoc);
            } catch (e) {
                console.error(e);
            }
            if (cancelled) return;
            const all = photoMode ? getLastLoadedFieldPhotos() : [];
            const fallback = photoMode && !looksLikeImage(first) ? undefined : first;
            const list = all.length ? all.slice(0, MAX_DOC_PHOTOS) : (fallback ? [fallback] : []);
            setPhotos(list);
            setPhotoIdx(0);
            showPhoto(list.length ? list[0] : fallback);
            if (photoMode) {
                onPhotoCountChange?.(list.length);
                setWithPhoto?.(list.length > 0);
                if (currentDoc) {
                    currentDoc.photo = list.length > 0;
                    currentDoc.photoCount = list.length;
                }
            } else {
                setWithPhoto?.(first != null);
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, pos, reload]);

    const gotoPhoto = (next: number) => {
        if (next < 0 || next >= photos.length) return;
        setPhotoIdx(next);
        setRotate(0);
        showPhoto(photos[next]);
    };

    const reloadPhotos = (doc: ExtendedDocument) => {
        const docType = doc.nkObject?.getConnectedDocType?.();
        if (docType == null) return;
        // Read the saved row from the server. The offline refresh treated a
        // חשבונית מס קבלה (type 1) as a credit note, so the list kept photo=false
        // and the camera disappeared on the next open.
        refresh.mutate({ids: [doc.id], types: [docType], offline: false});
        setReload((n) => n + 1);
    };

    const onFilesPicked = async (files: FileList | null) => {
        if (!files || files.length === 0 || !currentDoc) return;
        if (remaining <= 0) {
            alert(`ניתן להעלות עד ${MAX_DOC_PHOTOS} צילומים למסמך`);
            return;
        }
        const picked = Array.from(files).slice(0, remaining);
        setConverting(true);
        let known = photos.length;
        try {
            for (const file of picked) {
                const bytes = await fileToDocumentPhotoBytes(file);
                try {
                    await uploadDocumentPhoto(currentDoc, bytes);
                    known += 1;
                } catch (err) {
                    // The file is stored before the browser parses the note_sign
                    // row. A NULL signer makes that parse throw after a real save.
                    const loaded = (await getDocumentPhotos(currentDoc)).filter(looksLikeImage);
                    if (loaded.length <= known) throw err;
                    known = loaded.length;
                }
            }
            reloadPhotos(currentDoc);
        } catch (err) {
            console.error(err);
            reloadPhotos(currentDoc);
            alert(picked.some(isPdfFile)
                ? 'לא ניתן להמיר את קובץ ה-PDF לתמונה'
                : 'התמונה לא נשמרה');
        } finally {
            setConverting(false);
        }
    };

    const onDeleteCurrent = async () => {
        if (!currentDoc || photos.length === 0) return;
        if (!window.confirm('למחוק את הצילום?')) return;
        setLoading(true);
        try {
            await deleteDocumentPhoto(currentDoc, photoIdx);
            reloadPhotos(currentDoc);
        } catch (err) {
            console.error(err);
            alert('מחיקת הצילום נכשלה');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Tooltip title={tooltip}>
                <IconButton size='small' onClick={handleClickOpen}>
                    {icon}
                </IconButton>
            </Tooltip>
            <Dialog id='helper_dialog'
                    fullWidth={true}
                    maxWidth={'xl'}
                    open={open} onClose={handleClose}>
                <DialogTitle> {title}
                    <Tooltip title={tooltip}>
                        <IconButton size='small' onClick={() => {
                            if (imgByteArray)
                                saveByteArray(imgByteArray, 'תמונה', 'image/jpeg');
                        }}>
                            <DownloadIcon/>
                        </IconButton>
                    </Tooltip>
                    {photoMode ? <Tooltip
                        title={remaining > 0 ? `העלאת תמונה / PDF (${photos.length}/${MAX_DOC_PHOTOS})` : `מקסימום ${MAX_DOC_PHOTOS} צילומים`}>
                        <span>
                        <IconButton size='small' disabled={busy || remaining <= 0}
                                    onClick={() => uploadInputRef.current?.click()}>
                            <input ref={uploadInputRef} type="file" style={{display: 'none'}}
                                   multiple
                                   accept="image/*,application/pdf,.pdf"
                                   onChange={async (e) => {
                                       const input = e.target;
                                       await onFilesPicked(input.files);
                                       input.value = '';
                                   }}/>
                            {converting ? <CircularProgress size={20}/> : <UploadFile/>}
                        </IconButton>
                        </span>
                    </Tooltip> : undefined}
                    {photoMode && photos.length > 0 ? <Tooltip title={'מחק צילום'}>
                        <span>
                        <IconButton size='small' disabled={busy} onClick={onDeleteCurrent}>
                            <DeleteOutlineIcon/>
                        </IconButton>
                        </span>
                    </Tooltip> : undefined}
                    <Tooltip title={'סיבוב תמונה'}>
                        <IconButton size='small' onClick={() => {
                            setRotate(prevState => (prevState + 1) % 4)
                        }}>
                            <CameraswitchIcon/>
                        </IconButton>
                    </Tooltip>
                    {photoDocs && photoDocs.length > 1 && pos > 0 ?
                        <Tooltip title={'מסמך קודם'} style={{float: 'right'}}>
                            <IconButton size='small' disabled={busy} onClick={() => {
                                setPos(prevState => Math.max(0, prevState - 1))
                            }}>
                                <ArrowForwardIosIcon/>
                            </IconButton>
                        </Tooltip> : undefined}
                    {photoDocs && photoDocs.length > 1 && pos < photoDocs.length - 1 ?
                        <Tooltip title={'מסמך הבא'} style={{float: 'left'}}>
                            <IconButton size='small' disabled={busy} onClick={() => {
                                setPos(prevState => Math.min(photoDocs.length - 1, prevState + 1))
                            }}>
                                <ArrowBackIosNewIcon/>
                            </IconButton>
                        </Tooltip> : undefined}
                </DialogTitle>
                <DialogContent>
                    <Typography> {subtitle} </Typography>

                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: 12,
                        width: '100%'
                    }}>
                        {photoMode ?
                            <div style={{
                                flex: '0 0 48%',
                                maxWidth: '48%',
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                textAlign: 'center'
                            }}>
                                {manyPhotos ?
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8
                                    }}>
                                        <Tooltip title={'צילום קודם'}>
                                            <span>
                                            <IconButton size='small' disabled={busy || photoIdx <= 0}
                                                        onClick={() => gotoPhoto(photoIdx - 1)}>
                                                <ArrowForwardIosIcon/>
                                            </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Typography variant='body2'>{photoIdx + 1} / {photos.length}</Typography>
                                        <Tooltip title={'צילום הבא'}>
                                            <span>
                                            <IconButton size='small' disabled={busy || photoIdx >= photos.length - 1}
                                                        onClick={() => gotoPhoto(photoIdx + 1)}>
                                                <ArrowBackIosNewIcon/>
                                            </IconButton>
                                            </span>
                                        </Tooltip>
                                    </div>
                                    : undefined}
                                {imgSrc ?
                                    <Zoom key={photoIdx}>
                                        <img src={imgSrc} alt="" style={{
                                            maxWidth: '100%',
                                            maxHeight: '75vh',
                                            objectFit: 'contain',
                                            transform: `rotate(${rotate * 90}deg)`
                                        }}/>
                                    </Zoom>
                                    : <Typography variant='body2' style={{color: 'gray', padding: 24}}>
                                        {busy ? 'טוען צילום...' : 'אין צילום'}
                                    </Typography>}
                            </div>
                            : (imgSrc ?
                                <Zoom>
                                    <img src={imgSrc} alt="" style={{
                                        maxWidth: '100%',
                                        maxHeight: '80vh',
                                        objectFit: 'contain',
                                        transform: `rotate(${rotate * 90}deg)`
                                    }}/>
                                </Zoom>
                                : null)}
                        <div style={{flex: '1 1 auto', minWidth: 0, overflow: 'auto'}}>
                            {photoDocs ? <DocumentPreview document={photoDocs[pos]}/> : undefined}
                        </div>
                    </div>

                </DialogContent>
                <DialogActions>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default memo(ShowImageDialog);
